import React, { useCallback, useEffect, useRef } from 'react'
import { useWindowDimensions, View, ViewProps } from 'react-native'
import { BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ReduceMotion } from 'react-native-reanimated'
import { isNumber } from 'lodash'
import { createComponent } from 'react-native-small-ui'

import { isWeb, useMaxWidth } from '@/components/WebWrapper'
import { DS } from '@/design-system'

// Helpers
export const Center: ReturnType<typeof createComponent<ViewProps>> = createComponent(View, {
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center'
})

/**
 * Props for bottom sheet
 */
interface BaseBottomSheetProps {
  children: React.ReactNode
  /** Array of boolean values to watch - sheet dismisses when any become true */
  dismissOnOverlays?: boolean[]
  /** Callback when sheet index changes */
  onChange?: (index: number) => void
  /** Additional bottom inset (added to safe area) */
  bottomInset?: number
  /** Custom z-index (default: 10) */
  zIndex?: number
  /** Custom styles for the inner content container */
  contentContainerStyle?: object
}

/**
 * Base bottom sheet component with common configuration for game modals.
 * Handles auto-presentation, overlay dismissal, responsive sizing, and consistent styling.
 */
export const BaseBottomSheet = ({
  children,
  dismissOnOverlays = [],
  onChange,
  bottomInset = 24,
  zIndex = 10,
  contentContainerStyle
}: BaseBottomSheetProps) => {
  const insets = useSafeAreaInsets()
  const { width: screenWidth } = useWindowDimensions()
  const maxWidth = useMaxWidth()
  const _maxWidth = isNumber(maxWidth) ? maxWidth : screenWidth
  const marginHorizontal = screenWidth > _maxWidth ? (screenWidth - _maxWidth) / 2 : 0

  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const hasPresentedRef = useRef(false)

  // Check if any overlay is active
  const isBlocked = dismissOnOverlays.some((overlay) => overlay === true)

  // Dismiss when overlays are active, re-present when clear
  useEffect(() => {
    if (isBlocked) {
      bottomSheetModalRef.current?.dismiss()
    } else if (hasPresentedRef.current) {
      const timer = setTimeout(() => {
        bottomSheetModalRef.current?.present()
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [isBlocked])

  // Callback ref that only presents once
  const setBottomSheetRef = useCallback((ref: BottomSheetModal | null) => {
    bottomSheetModalRef.current = ref
    if (ref && !hasPresentedRef.current) {
      hasPresentedRef.current = true
      ref.present()
    }
  }, [])

  return (
    <BottomSheetModal
      ref={setBottomSheetRef}
      overrideReduceMotion={ReduceMotion.System}
      enableDynamicSizing={true}
      enableContentPanningGesture={false}
      enablePanDownToClose={false}
      enableHandlePanningGesture={false}
      enableOverDrag={false}
      backgroundStyle={{ backgroundColor: DS.palette['Brand/Cream'] }}
      handleComponent={null}
      bottomInset={insets.bottom || bottomInset}
      detached={true}
      onChange={onChange}
      containerStyle={{
        marginHorizontal: 8,
        marginLeft: !isWeb && marginHorizontal > 0 ? marginHorizontal : undefined,
        shadowColor: DS.palette['Shades/Maroon 2'],
        shadowOffset: {
          width: 0,
          height: 3
        },
        shadowOpacity: 0.27,
        shadowRadius: 4.65,
        zIndex,
        elevation: 6
      }}
      style={{
        marginHorizontal: 'auto',
        maxWidth: maxWidth
      }}
    >
      <BottomSheetView
        style={[
          {
            minHeight: 100,
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
            paddingHorizontal: 16,
            paddingVertical: 20,
            gap: 16
          },
          contentContainerStyle
        ]}
      >
        {children}
      </BottomSheetView>
    </BottomSheetModal>
  )
}
